import { api_fetch } from "./client"
import { Promotion } from "../dashboard/trade_promotion_management/promo_creation/types"

export interface DataCoverage {
  has_planned_spend: boolean
  has_actual_spend: boolean
  has_baseline: boolean
  has_pos_sales: boolean
  has_units: boolean
  has_margin: boolean
  has_support: boolean
  has_syndicated_metrics: boolean
  has_market_proxy_metrics: boolean
  has_case_volumes: boolean
}

export interface PromotionEffectivenessSummary {
  promotion_id: string
  remote_promo_id?: string | null
  name: string
  planned_spend: number
  actual_spend: number
  spend_variance: number
  total_sales?: number | null
  baseline_sales?: number | null
  incremental_sales?: number | null
  total_units?: number | null
  baseline_units?: number | null
  incremental_units?: number | null
  incremental_margin?: number | null
  lift_pct?: number | null
  lift_is_unit_based: boolean
  sales_efficiency?: number | null
  roi?: number | null
  cost_per_incremental_unit?: number | null
  data_coverage: DataCoverage
}

export interface PromotionEffectivenessPeriod {
  period_start: string
  period_end: string
  planned_spend: number
  actual_spend: number
  total_sales?: number | null
  baseline_sales?: number | null
  incremental_sales?: number | null
  total_units?: number | null
  baseline_units?: number | null
  incremental_units?: number | null
  incremental_margin?: number | null
  lift_pct?: number | null
  roi?: number | null
  support_label: string
  source_types: string[]
  has_market_proxy: boolean
}

export interface PromotionPeerEffectiveness {
  promotion_id: string
  remote_promo_id?: string | null
  name: string
  customer?: string | null
  retailer?: string | null
  promotion_type?: string | null
  planned_spend: number
  actual_spend: number
  spend_variance: number
  incremental_sales?: number | null
  lift_pct?: number | null
  roi?: number | null
  sales_efficiency?: number | null
}

export interface PromotionBumpChartPoint {
  promotion_id: string
  promotion_name: string
  metric: string
  period: string
  rank: number
  value?: number | null
}

export interface PromotionPerformanceMetricRow {
  id: string
  promotion_id: string
  source_file_id?: string | null
  source_type: string
  source_report_id?: string | null
  source_row_ids: unknown
  match_confidence?: number | null
  match_reason?: string | null
  period_start: string
  period_end: string
  customer?: string | null
  retailer?: string | null
  product?: string | null
  market?: string | null
  total_sales_dollars?: number | null
  baseline_sales_dollars?: number | null
  incremental_sales_dollars?: number | null
  total_units?: number | null
  baseline_units?: number | null
  incremental_units?: number | null
  incremental_margin_dollars?: number | null
  gross_margin_pct?: number | null
  acv?: number | null
  tdp?: number | null
  promo_price?: number | null
  regular_price?: number | null
  discount_depth_pct?: number | null
  feature_support?: boolean | null
  display_support?: boolean | null
  notes?: string | null
}

export interface PromotionEffectivenessResponse {
  promotion: Promotion
  summary: PromotionEffectivenessSummary
  periods: PromotionEffectivenessPeriod[]
  peers: PromotionPeerEffectiveness[]
  bump_chart: PromotionBumpChartPoint[]
  missing_data: string[]
  metric_rows: PromotionPerformanceMetricRow[]
}

export interface PromotionPerformanceImportResponse {
  imported_rows: number
  skipped_rows: string[]
  files: unknown[]
}

export interface SyndicatedReportOption {
  id: string
  source: string
  report_type: string
  report_date: string
  file_name: string
  upload_brand_name?: string | null
}

export interface DeriveSyndicatedEffectivenessResponse {
  imported_rows: number
  skipped_rows: string[]
  candidate_rows: Array<{
    promotion_id: string
    promotion_name: string
    report_row_id: string
    product: string
    market: string
    confidence: number
    reason: string
    accepted: boolean
  }>
}

export interface PromotionEffectivenessSeedResponse {
  calendar_id: string
  report_id: string
  promotion_count: number
  metric_rows: number
  deduction_count: number
}

export type PromotionRankMetric = "roi" | "lift_pct" | "incremental_sales" | "actual_spend" | "spend_variance"

export type PromotionPerformanceMetricFocus = "sales" | "margin" | "all"

export interface PromotionPerformanceMetricInput {
  period_start?: string | null
  period_end?: string | null
  customer?: string | null
  retailer?: string | null
  product?: string | null
  market?: string | null
  total_sales_dollars?: number | null
  baseline_sales_dollars?: number | null
  incremental_sales_dollars?: number | null
  total_units?: number | null
  baseline_units?: number | null
  incremental_units?: number | null
  incremental_margin_dollars?: number | null
  gross_margin_pct?: number | null
  acv?: number | null
  tdp?: number | null
  promo_price?: number | null
  regular_price?: number | null
  discount_depth_pct?: number | null
  feature_support?: boolean | null
  display_support?: boolean | null
  notes?: string | null
}

export async function getPromotionEffectiveness(
  promotion_id: string,
  rank_metric: PromotionRankMetric = "roi",
): Promise<PromotionEffectivenessResponse> {
  const result = await api_fetch<PromotionEffectivenessResponse>(`/promotions/${promotion_id}/effectiveness`, {
    params: { rank_metric },
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to load promotion insights: ${result.error.message}`)
}

export async function uploadPromotionEffectiveness(promotion_id: string, files: File[]): Promise<PromotionPerformanceImportResponse> {
  const form_data = new FormData()
  files.forEach((file: File) => form_data.append("files", file))
  const response = await fetch(`/api/promotions/${promotion_id}/effectiveness/import`, {
    method: "POST",
    credentials: "same-origin",
    body: form_data,
  })
  if (!response.ok) {
    throw new Error(await response.text())
  }
  return await response.json()
}

export async function createPromotionPerformanceMetric(
  promotion_id: string,
  body: PromotionPerformanceMetricInput,
): Promise<PromotionPerformanceMetricRow> {
  const result = await api_fetch<PromotionPerformanceMetricRow>(`/promotions/${promotion_id}/effectiveness/metrics`, {
    method: "POST",
    body,
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to create performance metric: ${result.error.message}`)
}

export async function updatePromotionPerformanceMetric(
  promotion_id: string,
  metric_id: string,
  body: PromotionPerformanceMetricInput,
): Promise<PromotionPerformanceMetricRow> {
  const result = await api_fetch<PromotionPerformanceMetricRow>(`/promotions/${promotion_id}/effectiveness/metrics/${metric_id}`, {
    method: "PATCH",
    body,
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to update performance metric: ${result.error.message}`)
}

export async function deletePromotionPerformanceMetric(promotion_id: string, metric_id: string): Promise<void> {
  const result = await api_fetch<void>(`/promotions/${promotion_id}/effectiveness/metrics/${metric_id}`, {
    method: "DELETE",
  })
  if (result.ok) return
  throw new Error(`Failed to delete performance metric: ${result.error.message}`)
}

export async function listSyndicatedReports(): Promise<SyndicatedReportOption[]> {
  const result = await api_fetch<SyndicatedReportOption[]>("/distributor-sales-reports", {
    method: "GET",
    params: { source: "spins", limit: 25 },
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to load syndicated reports: ${result.error.message}`)
}

export async function deriveSyndicatedPromotionEffectiveness(
  calendar_id: string,
  report_id: string,
): Promise<DeriveSyndicatedEffectivenessResponse> {
  const result = await api_fetch<DeriveSyndicatedEffectivenessResponse>(`/promotional-calendars/${calendar_id}/effectiveness/syndicated`, {
    method: "POST",
    body: { report_id },
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to derive syndicated promotion metrics: ${result.error.message}`)
}

export async function generatePromotionEffectivenessSeedData(): Promise<PromotionEffectivenessSeedResponse> {
  const result = await api_fetch<PromotionEffectivenessSeedResponse>("/promotional-calendars/effectiveness/seed-data", {
    method: "POST",
    body: {},
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to create TPM dataset: ${result.error.message}`)
}
